import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Plus,
  Trash2,
  Save,
  Variable,
  FileText,
  Tag,
  Hash,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
  DialogFooter,
} from '@/components/ui/dialog';
import { useModuleStore } from '@/stores/module-store';
import type { CRMField } from '@/types/saas';
import type { MessageTemplate, Channel } from '@/types/messaging';

/** Data types that produce meaningful text values for message placeholders */
const PLACEHOLDER_DATA_TYPES = new Set([
  'text', 'phone', 'email', 'number', 'currency', 'double',
  'date', 'datetime', 'website', 'textarea', 'bigint', 'autonumber',
  'integer', 'picklist', 'multiselectpicklist', 'lookup', 'ownerlookup',
]);

interface TemplateBuilderProps {
  templates: MessageTemplate[];
  channel: Channel;
  onSave: (template: Omit<MessageTemplate, 'id'>) => Promise<void>;
  onDelete?: (templateId: string) => Promise<void>;
}

export function TemplateBuilder({ templates, channel, onSave, onDelete }: TemplateBuilderProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);

  const handleEdit = (template: MessageTemplate) => {
    setEditingTemplate(template);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setEditingTemplate(null);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-foreground">Message Templates</h3>
          <p className="text-sm text-muted-foreground">
            Create and manage reusable message templates
          </p>
        </div>
        <Button size="sm" onClick={handleNew}>
          <Plus className="h-3.5 w-3.5" />
          New Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="h-8 w-8 text-muted-foreground mx-auto" />
            <p className="text-sm text-muted-foreground mt-3">
              No templates yet. Create your first template to speed up messaging.
            </p>
            <Button size="sm" className="mt-4" onClick={handleNew}>
              <Plus className="h-3.5 w-3.5" />
              Create Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {templates.map((template) => (
            <Card key={template.id} className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="p-4" onClick={() => handleEdit(template)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm font-medium truncate">{template.name}</h4>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {template.body}
                    </p>
                  </div>
                  {onDelete && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(template.id);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {template.category && (
                    <Badge variant="secondary" className="text-[10px]">
                      <Tag className="h-2.5 w-2.5 mr-0.5" />
                      {template.category}
                    </Badge>
                  )}
                  {template.variables && template.variables.length > 0 && (
                    <Badge variant="outline" className="text-[10px]">
                      <Variable className="h-2.5 w-2.5 mr-0.5" />
                      {template.variables.length} var{template.variables.length !== 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <TemplateEditorDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        template={editingTemplate}
        channel={channel}
        onSave={onSave}
      />
    </div>
  );
}

export function TemplateEditorDialog({
  open,
  onOpenChange,
  template,
  channel,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: MessageTemplate | null;
  channel: Channel;
  onSave: (template: Omit<MessageTemplate, 'id'> & { saasModule?: string }) => Promise<void>;
}) {
  const [name, setName] = useState(template?.name || '');
  const [body, setBody] = useState(template?.body || '');
  const [selectedModule, setSelectedModule] = useState('');
  const [saving, setSaving] = useState(false);

  // Module fields for # autocomplete
  const [moduleFields, setModuleFields] = useState<CRMField[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);

  // # trigger autocomplete state
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hashMenuRef = useRef<HTMLDivElement>(null);
  const [hashMenuOpen, setHashMenuOpen] = useState(false);
  const [hashSearch, setHashSearch] = useState('');
  const [hashStartIndex, setHashStartIndex] = useState(-1);

  // Get supported modules from SaaS module + CURRENT_USER
  const saasModule = useModuleStore((s) => s.saasModule);
  const supportedModules = useMemo(() => {
    const modules = saasModule ? [...saasModule.getSupportedModules()] : [];
    modules.push({ value: 'CURRENT_USER', label: 'Current User' });
    return modules;
  }, [saasModule]);

  // Default fields available when no module is selected
  const currentUserFields: CRMField[] = useMemo(() => [
    { apiName: 'name', displayLabel: 'Current User — Name', dataType: 'text', required: false, readOnly: true },
    { apiName: 'email', displayLabel: 'Current User — Email', dataType: 'email', required: false, readOnly: true },
    { apiName: 'phone', displayLabel: 'Current User — Phone', dataType: 'phone', required: false, readOnly: true },
  ], []);

  // Filter fields to meaningful data types
  const filteredFields = useMemo(() => {
    if (moduleFields.length === 0) return selectedModule ? [] : currentUserFields;
    return moduleFields.filter((f) => PLACEHOLDER_DATA_TYPES.has(f.dataType));
  }, [moduleFields, selectedModule, currentUserFields]);

  // Filter fields for # autocomplete search
  const hashFilteredFields = useMemo(() => {
    if (!hashSearch) return filteredFields;
    const q = hashSearch.toLowerCase();
    return filteredFields.filter(
      (f) =>
        f.displayLabel.toLowerCase().includes(q) ||
        f.apiName.toLowerCase().includes(q)
    );
  }, [filteredFields, hashSearch]);

  // Reset form when dialog opens
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setName(template?.name || '');
      setBody(template?.body || '');
      setSelectedModule('');
      setModuleFields([]);
      setHashMenuOpen(false);
      setHashSearch('');
      setHashStartIndex(-1);
    }
    onOpenChange(isOpen);
  };

  // Fetch module fields when module is selected
  useEffect(() => {
    if (!selectedModule) {
      setModuleFields([]);
      return;
    }
    // CURRENT_USER — static fields, no API call
    if (selectedModule === 'CURRENT_USER') {
      setModuleFields([
        { apiName: 'name', displayLabel: 'Name', dataType: 'text', required: false, readOnly: true },
        { apiName: 'email', displayLabel: 'Email', dataType: 'email', required: false, readOnly: true },
        { apiName: 'phone', displayLabel: 'Phone', dataType: 'phone', required: false, readOnly: true },
      ]);
      setFieldsLoading(false);
      return;
    }
    if (!saasModule) {
      setModuleFields([]);
      return;
    }
    let cancelled = false;
    setFieldsLoading(true);
    saasModule.getModuleFields(selectedModule).then((fields) => {
      if (!cancelled) {
        setModuleFields(fields);
        setFieldsLoading(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setModuleFields([]);
        setFieldsLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [selectedModule, saasModule]);

  // --- # trigger handlers ---
  const handleHashSelect = useCallback((apiName: string) => {
    const ta = textareaRef.current;
    // Determine placeholder format based on context
    const isCurrentUserField = !selectedModule && currentUserFields.some(f => f.apiName === apiName);
    const placeholder = selectedModule
      ? `\${${selectedModule}.${apiName}}`
      : isCurrentUserField
        ? `\${CURRENT_USER.${apiName}}`
        : `{{${apiName}}}`;
    if (ta && hashStartIndex >= 0) {
      const cursorPos = ta.selectionStart ?? body.length;
      const newValue = body.slice(0, hashStartIndex) + placeholder + body.slice(cursorPos);
      setBody(newValue);
      requestAnimationFrame(() => {
        ta.focus();
        const newPos = hashStartIndex + placeholder.length;
        ta.setSelectionRange(newPos, newPos);
      });
    }
    setHashMenuOpen(false);
    setHashSearch('');
    setHashStartIndex(-1);
  }, [body, hashStartIndex]);

  const handleTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    const cursorPos = e.target.selectionStart ?? val.length;
    setBody(val);

    // Look backwards from cursor for a # token
    const textBeforeCursor = val.slice(0, cursorPos);
    const hashIdx = textBeforeCursor.lastIndexOf('#');

    if (hashIdx >= 0 && filteredFields.length > 0) {
      const tokenAfterHash = textBeforeCursor.slice(hashIdx + 1);
      if (!/\s/.test(tokenAfterHash)) {
        setHashMenuOpen(true);
        setHashSearch(tokenAfterHash);
        setHashStartIndex(hashIdx);
        return;
      }
    }
    setHashMenuOpen(false);
    setHashSearch('');
    setHashStartIndex(-1);
  }, [filteredFields]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (hashMenuOpen && e.key === 'Escape') {
      e.preventDefault();
      setHashMenuOpen(false);
      setHashSearch('');
      setHashStartIndex(-1);
    }
  }, [hashMenuOpen]);

  // Close hash menu on outside click
  useEffect(() => {
    if (!hashMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (hashMenuRef.current && !hashMenuRef.current.contains(e.target as Node)) {
        setHashMenuOpen(false);
        setHashSearch('');
        setHashStartIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [hashMenuOpen]);

  const handleSave = async () => {
    if (!name.trim() || !body.trim()) return;
    setSaving(true);
    try {
      // Auto-extract variables from {{...}} in body
      const varMatches = body.match(/\{\{(\w+)\}\}/g);
      const variables = varMatches
        ? Array.from(new Set(varMatches.map((m) => m.replace(/\{\{|\}\}/g, ''))))
        : undefined;

      await onSave({
        name: name.trim(),
        body: body.trim(),
        channel,
        variables,
        saasModule: selectedModule || undefined,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader>
          <DialogTitle>{template ? 'Edit Template' : 'New Template'}</DialogTitle>
          <DialogDescription>
            {template ? 'Modify your message template' : 'Create a reusable message template'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto">
          {/* Template Name */}
          <div className="space-y-1.5">
            <Label>Template Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Welcome Message"
            />
          </div>

          {/* Module selector */}
          {supportedModules.length > 0 && (
            <div className="space-y-1.5">
              <Label>Module</Label>
              <select
                value={selectedModule}
                onChange={(e) => setSelectedModule(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="">Select module...</option>
                {supportedModules.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Choose a module to enable field placeholders
              </p>
            </div>
          )}

          {/* Message body with # autocomplete */}
          <div className="space-y-1.5">
            <Label>Message Body</Label>
            <div className="relative">
              <Textarea
                ref={textareaRef}
                value={body}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                placeholder={
                  filteredFields.length > 0
                    ? 'Type your message. Type # to insert field placeholders.'
                    : 'Type your message.'
                }
                rows={3}
                className="text-sm resize-none overflow-hidden"
                style={{ height: 'auto', minHeight: '4.5rem' }}
                onInput={(e) => { const t = e.currentTarget; t.style.height = 'auto'; t.style.height = t.scrollHeight + 'px'; }}
              />

              {/* # trigger floating menu */}
              {hashMenuOpen && hashFilteredFields.length > 0 && (
                <div
                  ref={hashMenuRef}
                  className="absolute left-0 bottom-full mb-1 z-50 w-full max-h-48 overflow-y-auto rounded-lg border border-border bg-popover shadow-lg scrollbar-thin"
                >
                  <div className="p-1">
                    {hashFilteredFields.map((f) => (
                      <button
                        key={f.apiName}
                        type="button"
                        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleHashSelect(f.apiName);
                        }}
                      >
                        <Hash className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="truncate">{f.displayLabel}</span>
                        <span className="ml-auto text-xs text-muted-foreground truncate">{f.apiName}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {body.length} characters
              {selectedModule && filteredFields.length > 0 && (
                <>
                  {' \u2014 Type '}
                  <kbd className="px-1 py-0.5 rounded bg-muted border text-[10px] font-mono">#</kbd>
                  {' to insert field placeholders'}
                </>
              )}
              {fieldsLoading && ' \u2014 Loading fields...'}
            </p>
          </div>

          {/* Preview */}
          {body && (
            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <p className="text-xs font-medium text-muted-foreground mb-1">Preview</p>
              <p className="text-sm text-foreground whitespace-pre-wrap">
                {body.replace(/\{\{(\w+)\}\}/g, (_match, v) => {
                  const field = moduleFields.find((f) => f.apiName === v);
                  return `[${field?.displayLabel || v}]`;
                })}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !name.trim() || !body.trim()}>
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : template ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
